"use client";

import { useEffect } from "react";

import {
  analyticsEnabled,
  ANALYTICS_EVENTS,
  disableAnalytics,
  initAnalytics,
  track,
  type AnalyticsEvent,
} from "@/lib/analytics";
import { useConsent } from "@/lib/consent-context";
import { useIsEmbedded } from "@/lib/embedded";
import { regionFromPathname } from "@/lib/region";
import { usePublicPathname } from "@/lib/public-pathname";

export function AmplitudeAnalytics() {
  const { consent, hasDecided } = useConsent();
  const pathname = usePublicPathname();
  const region = regionFromPathname(pathname);
  const embedded = useIsEmbedded();
  const allowed = hasDecided && consent.statistics && !embedded;

  useEffect(() => {
    if (!analyticsEnabled()) return;
    if (allowed) initAnalytics({ region });
    else if (hasDecided) disableAnalytics();
  }, [allowed, hasDecided, region]);

  useEffect(() => {
    if (!analyticsEnabled() || embedded) return;
    if (!/\/laser-lifetime-care-guarantee\/?$/.test(pathname)) return;
    track(ANALYTICS_EVENTS.LCG_VIEWED, { region, path: pathname });
  }, [pathname, region, embedded]);

  useEffect(() => {
    if (!analyticsEnabled() || embedded) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;

      const tracked = target?.closest?.("[data-track-event]");
      if (tracked) {
        const event = tracked.getAttribute("data-track-event");
        if (!event) return;
        const props: Record<string, string> = {};
        for (const attr of Array.from(tracked.attributes)) {
          if (attr.name.startsWith("data-track-") && attr.name !== "data-track-event") {
            props[attr.name.slice("data-track-".length)] = attr.value;
          }
        }
        track(event as AnalyticsEvent, props);
        return;
      }

      const a = target?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href") || "";
      const label = (a.textContent || "").trim().slice(0, 80);
      const linkRegion = regionFromPathname(window.location.pathname);

      if (href.startsWith("tel:")) {
        track(ANALYTICS_EVENTS.PHONE_CLICKED, { href, region: linkRegion });
        return;
      }
      if (/^https?:\/\//i.test(href)) {
        let external = false;
        try {
          external = new URL(href).host !== window.location.host;
        } catch {}
        if (external) {
          track(ANALYTICS_EVENTS.OUTBOUND_LINK_CLICKED, { href, label });
          return;
        }
      }
      const navEl = a.closest("nav, footer, [role='navigation']");
      if (navEl) {
        track(ANALYTICS_EVENTS.NAVIGATION_CLICKED, {
          label,
          href,
          location: navEl.getAttribute("aria-label") || navEl.tagName.toLowerCase(),
          region: linkRegion,
        });
      }
    };
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, [embedded]);

  return null;
}
